import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// Единый диалог подтверждения вместо window.confirm().
// Использование:
//   const confirm = useConfirm();
//   if (!(await confirm({ title: 'Удалить базу?', message: 'Действие необратимо.', confirmText: 'Удалить', danger: true }))) return;

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialog({
        title: 'Подтвердите действие',
        message: '',
        confirmText: 'Подтвердить',
        cancelText: 'Отмена',
        danger: false,
        ...options,
      });
    });
  }, []);

  const close = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setDialog(null);
  }, []);

  useEffect(() => {
    if (!dialog) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dialog, close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => close(false)} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6" role="dialog" aria-modal="true">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{dialog.title}</h3>
            {dialog.message && (
              <p className="text-sm text-gray-600 mb-6 whitespace-pre-line">{dialog.message}</p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => close(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                {dialog.cancelText}
              </button>
              <button
                onClick={() => close(true)}
                autoFocus
                className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${
                  dialog.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {dialog.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm должен вызываться внутри <ConfirmProvider>');
  return ctx;
}
